import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { login as loginService } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

const Login = () => {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  const { email, password } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailVerificationRequired(false);
    setLoading(true);

    console.log('🔵 Login form submitted with email:', email);

    try {
      const response = await loginService({ email, password });
      console.log('✅ Login response:', response);
      console.log('👤 User data:', response.user);
      console.log('📊 User status:', response.user.status);
      console.log('🔑 User role:', response.user.role);
      
      login(response.user, response.token);
      console.log('✅ Auth context updated');
      
      // Check user status and redirect accordingly
      if (response.user.status === 'pending') {
        console.log('➡️  Redirecting to /pending-approval');
        navigate('/pending-approval');
      } else if (response.user.status === 'rejected') {
        console.log('❌ User rejected, clearing auth');
        setError(response.user.rejectionReason || 'Hesabınız reddedildi. Lütfen destek ekibiyle iletişime geçin.');
        logout(); // Clear auth state
      } else if (response.user.status === 'approved' && !response.user.isEmailVerified) {
        console.log('📧 Email not verified, redirecting to verify-email-pending');
        navigate('/verify-email-pending');
      } else if (response.user.status === 'approved' && response.user.isEmailVerified) {
        console.log('➡️  Redirecting to /dashboard');
        navigate('/dashboard');
      } else {
        console.log('⚠️  Unknown status, redirecting to /dashboard');
        navigate('/dashboard'); // Fallback
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      console.error('❌ Error response:', err.response);
      const errorData = err.response?.data;
      setError(errorData?.message || 'Giriş yapılırken bir hata oluştu');
      
      if (errorData?.emailVerificationRequired) {
        setEmailVerificationRequired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Logo showText={false} className="h-20 w-20" />
          </div>
          <h1 className="text-4xl font-bold mb-2">
            <span className="text-gray-900">Deneme</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-purple-600">Koç</span>
          </h1>
          <h2 className="text-2xl font-semibold text-gray-900">Giriş Yap</h2>
          <p className="mt-2 text-sm text-gray-600">
            YKS deneme sonuçlarınızı takip edin
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form className="space-y-6" onSubmit={onSubmit}>
            {error && (
              <div className="bg-error-50 border border-error-200 text-error-800 rounded-lg p-4 text-sm">
                <p className="font-medium">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Adresi
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={onChange}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition duration-150"
                placeholder="ornek@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Şifre
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={onChange}
                className="appearance-none relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition duration-150"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Beni hatırla
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-600 hover:text-primary-500 transition duration-150"
                >
                  Şifremi unuttum
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Giriş Yap'
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Şifremi Unuttum?
            </Link>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Hesabınız yok mu?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/register"
                className="w-full flex justify-center py-3 px-4 border-2 border-primary-600 rounded-lg shadow-sm text-sm font-semibold text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-150"
              >
                Kayıt Ol
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
